"use client";

import styles from "@/styles/product-detail/product-shipping.module.scss";
import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import Dialog from "@/components/global/Dialog";
import { toast } from "@/components/global/Toast";
import NotifyDialog from "@/components/product-detail/NotifyDialog";
import WholesaleDialog from "@/components/product-detail/WholesaleDialog";
import ExpediteDialog from "@/components/global/ExpediteDialog";
import { Button } from "react-vant";
import productApi from "@/service/modules/productApi";
import { setGtmDataLayer } from "@/utils/gtmDataLayer";
import { errorUtils } from "@/utils/error";

import {
  ProductType,
  PostageDataType,
  paymentMethodItem,
  SpecOptionType,
  BreadCrumbItem,
} from "@/types/product";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomerInfo } from "@/store/slices/userSlice";
import useCartActions from "@/utils/addTocartAction";
import dayjs from "dayjs";
import SuburbSelectDropdown from "../global/SuburbSelectDropdown";
import { useSelector } from "react-redux";
import { RootState } from "@/store/store";
import {
  isPaymentMethodVisible,
  ZIP_PAYMENT_ENABLED,
} from "@/utils/paymentAvailability";

interface Props {
  productDetailData: ProductType;
  paymentMethod: paymentMethodItem[];
  breadCrumb: BreadCrumbItem[];
}
export default function ProductShipping({
  productDetailData,
  paymentMethod,
  breadCrumb,
}: Props) {
  const dispatch = useAppDispatch();
  const { addToCart } = useCartActions();
  // 从Redux store获取用户状态
  const { customerInfo, hasLogin } = useAppSelector((state) => state.user);
  // 组件挂载时获取用户信息
  useEffect(() => {
    dispatch(fetchCustomerInfo());
  }, [dispatch]);

  const [quantity, setQuantity] = useState(1);
  const [showAfterPayDialog, setShowAfterPayDialog] = useState(false);
  const [showZippayDialog, setShowZippayDialog] = useState(false);
  const [showCombineModal, setShowCombineModal] = useState(false);

  const [multipleSpecList, setMultipleSpecList] = useState<SpecOptionType[]>(
    [],
  );

  const defaultSuburbID = useSelector(
    (state: RootState) => state.user.customerInfo.suburbID,
  );
  const defaultSuburbName = useSelector(
    (state: RootState) => state.user.customerInfo.suburb,
  );
  const [suburbId, setSuburbId] = useState<any>("");
  const [suburbName, setSuburbName] = useState<string>("");

  const initialPostageData: PostageDataType = {
    courier: "",
    courierId: "",
    dTime: "",
    itemError: "",
    postage: "",
    productID: "",
    quantity: 1,
    eta: "",
  };
  const [postageData, setPostageData] =
    useState<PostageDataType>(initialPostageData);

  const [postageLoading, setPostageLoading] = useState(false);
  const [shippingError, setShippingError] = useState(false);
  const [shippingDistanceError, setShippingDistanceError] = useState(false);

  // 加入wishlist
  const [isWish, setIsWish] = useState(false);
  const [wpid, setWpid] = useState(0);
  const [addWishlistLoading, setAddWishlistLoading] = useState(false);
  const [showWishLoginModal, setShowWishLoginModal] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showWholesaleModal, setShowWholesaleModal] = useState(false);

  // 设置谷歌GTM dataLayer
  useEffect(() => {
    function handleWindowLoad() {
      let ecomm_catname = "";
      if (breadCrumb && breadCrumb.length > 0) {
        ecomm_catname = breadCrumb.map((v: any) => v.name).join(" > ");
      }

      const g_params = {
        event: "productDetailPage",
        remarketing_params: {
          ecomm_prodid: productDetailData?.productID,
          ecomm_pagetype: "product",
          ecomm_totalvalue: productDetailData?.price,
          ecomm_prodname: productDetailData?.name,
          ecomm_catname: ecomm_catname,
          ecomm_email: "",
          ecomm_sku: productDetailData?.productCode,
        },
        params: {
          userId: customerInfo.customerID,
          categoryId: null,
          productId: productDetailData?.productID,
        },
      };

      setGtmDataLayer(g_params);
    }

    window.addEventListener("load", handleWindowLoad);

    return () => {
      window.removeEventListener("load", handleWindowLoad);
    };
  }, []);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newQuantity = parseInt(e.target.value, 10);
    setQuantity(newQuantity);
  };

  const stockQuantity = Number(productDetailData?.inStock || 0);
  const isPreOrder = !!productDetailData?.preSales;
  const isProductEnabled = !!productDetailData?.enabled;
  const isSoldOut = !isPreOrder && (!isProductEnabled || stockQuantity <= 0);
  const isAlmostSoldOut =
    !isPreOrder && isProductEnabled && stockQuantity > 0 && stockQuantity <= 5;
  const isInStock = !isPreOrder && isProductEnabled && stockQuantity > 5;
  const isQuantityDisabled =
    !isProductEnabled || (!isPreOrder && stockQuantity <= 0);
  const quantityLimit =
    stockQuantity >= 100 ? 100 : Math.max(1, stockQuantity || 1);

  useEffect(() => {
    if (quantity) {
      // 重新计算邮费
    }
  }, [quantity]);

  useEffect(() => {
    if (defaultSuburbID) {
      setSuburbId(parseInt(`${defaultSuburbID}`));
      setSuburbName(defaultSuburbName);
    }
  }, [defaultSuburbID]);

  // 监听suburb，触发更新邮费
  useEffect(() => {
    if (suburbId) {
      calculatePostage();
    }
  }, [suburbId]);

  // 监听quantity，触发更新邮费
  useEffect(() => {
    if (quantity) {
      calculatePostage();
    }
  }, [quantity]);

  const suburbChange = (data: any, option: any) => {
    setSuburbId(data);
    setSuburbName(option.name);
  };

  // 获取邮费数据
  const calculatePostage = async () => {
    // 没有选中currentSuburb，不请求
    if (!suburbId) return;

    try {
      setPostageLoading(true);
      const dataObj = {
        suburb: suburbName,
        suburbID: suburbId,
        productID: productDetailData?.productID,
        quantity: quantity,
      };
      const res = await productApi.getShippingData(dataObj);
      setPostageLoading(false);
      setShippingDistanceError(false);
      setShippingError(false);
      if (res.data) {
        setPostageData(res.data);
      }
    } catch (error: any) {
      if (error.code == 150002) {
        setShippingDistanceError(true);
        setShippingError(false);
      } else {
        setShippingDistanceError(false);
        setShippingError(true);
      }
      setPostageLoading(false);
      setPostageData(initialPostageData);
    }
  };

  const handleShowCombineModal = () => {
    setShowCombineModal(true);
  };

  // 多规格属性 配置初始化
  useEffect(() => {
    if (productDetailData?.variants && productDetailData?.variants.options) {
      // 提取所有选项到一个数组
      const optionsArray: SpecOptionType[] = [];

      if (productDetailData?.variants.options) {
        const optionKeys = Object.keys(productDetailData.variants.options);

        optionKeys.forEach((key) => {
          if (productDetailData.variants.options[key].length) {
            optionsArray.push({
              name: key,
              defaultVariant: 0,
              optionID: productDetailData.variants.options[key][0].optionID,
              options: productDetailData.variants.options[key],
            });
          }
        });
      }

      // 选中variants.products里面产品ID相同的options，赋值。没得赋值就添加一个选项：Please Select
      const target = productDetailData?.variants.products.filter(
        (item: any) => item.productID == productDetailData.productID,
      );
      if (target.length && target[0].options) {
        target[0].options.forEach((option: any) => {
          if (optionsArray && optionsArray.length) {
            optionsArray.forEach((spec: SpecOptionType) => {
              if (option.optionID == spec.optionID) {
                spec.defaultVariant = option.variantID;
              }
            });
          }
        });
      } else if (
        target.length &&
        (!target[0].options || target[0].options.length <= 0)
      ) {
        // 如果没有默认值（数据原因），则加上Please Select选项
        optionsArray.forEach((spec: SpecOptionType) => {
          spec.options?.unshift({
            optionValue: "Please Select",
            additionalValue: "#008000",
            optionID: spec.optionID,
            variantID: 0,
          });
        });
      }

      setMultipleSpecList(optionsArray);
    }
  }, []);

  // 触发更新Spec，跳转到对应的链接
  const handleChangeSpec = (optionID: any, value: number) => {
    // 先复制一份商品列表出来，格式化为前端容易做匹配的格式
    const specProduct = JSON.parse(
      JSON.stringify(productDetailData?.variants.products),
    );

    // 遍历当前选中值的集合，方便后面做匹配
    const selectedOptions = multipleSpecList.map((item) => {
      return {
        optionID: item.optionID,
        variantID: item.defaultVariant,
      };
    });

    // 把刚刚选中的值，更新到selectedOptions
    selectedOptions.forEach((item) => {
      if (item.optionID == optionID) {
        item.variantID = value;
      }
    });

    // 存储匹配的结果 productUrl
    let matchingProductUrl = null;

    // 遍历产品数据，有结果就赋值给matchingProductUrl
    for (const product of specProduct) {
      const productOptions = product.options;

      // 检查 productOptions 是否为空
      if (!Array.isArray(productOptions) || productOptions.length === 0) {
        continue; // 如果 productOptions 为空，跳过该产品
      }

      // 检查当前产品的选项是否与选中值匹配
      if (
        selectedOptions.every((selectedOption) =>
          productOptions.some(
            (productOption: any) =>
              productOption.optionID === selectedOption.optionID &&
              productOption.variantID === selectedOption.variantID,
          ),
        )
      ) {
        matchingProductUrl = product.url;
        break; // 找到匹配后，立即停止遍历
      }
    }

    // 跳转到选中的URL
    if (matchingProductUrl) {
      window.location.href = matchingProductUrl;
    }
  };

  // 商品销售状态管理，return对应的操作按钮
  const StockStatusBtn = () => {
    if (productDetailData?.preSales) {
      return (
        // 预售按钮
        <Button
          type="primary"
          block
          className={styles["pre-sales-btn"]}
          onClick={preOrderNow}
        >
          Pre Sales
        </Button>
      );
    } else {
      if (productDetailData?.inStock && productDetailData?.enabled) {
        // 正常可购买
        return (
          <Button
            type="primary"
            block
            className={styles["add-cart"]}
            onClick={(event) => {
              addToCart(
                event,
                {
                  productID: productDetailData.productID,
                  qty: quantity,
                },
                (res: any) => {
                  addToCartCallBack(res);
                },
              );
            }}
          >
            Buy Now
          </Button>
        );
      } else if (
        !productDetailData?.enabled &&
        productDetailData?.discontinued
      ) {
        return (
          <Button
            type="primary"
            block
            className={styles["add-cart"]}
          >
            Unavailable
          </Button>
        );
      } else {
        return (
          <Button
            type="primary"
            block
            className={styles["add-cart"]}
            onClick={() => setShowNotifyModal(true)}
          >
            Notify Me
          </Button>
        );
      }
    }
  };

  // PRE-ORDER NOW
  function preOrderNow() {
    const href = `/checkout?p=${productDetailData?.productID}&t=${quantity}`;
    const expressHref = `/checkout/express?p=${productDetailData?.productID}&t=${quantity}`;

    if (hasLogin) {
      window.location.href = href;
    } else {
      window.location.href = expressHref;
    }
  }

  const addToCartCallBack = (response: any) => {
    if (response != "error") {
      window.location.href = "/view-cart";
    }
  };

  // 获取是否有加入wishlist
  useEffect(() => {
    const getWishData = async () => {
      try {
        const res = await productApi.getIsWishByProduct(
          productDetailData?.productID,
        );
        if (res.data) {
          setIsWish(!!res.data.isWish);

          // 保存下商品的心愿单id。后续remove操作用得上
          if (res.data.isWish) setWpid(res.data.wpid);
        }
      } catch (error) {
        console.error(error);
      }
    };

    if (hasLogin) {
      getWishData();
    }
  }, [hasLogin]);

  // 加入/移除 wishlist
  const handleToggleWish = async () => {
    if (isWish) {
      try {
        if (!wpid) {
          toast.show("System Error");
          return;
        }

        setAddWishlistLoading(true);
        const res = await productApi.removeWishList(wpid);
        if (res.data) {
          toast.show("Removed successfully.");
          setIsWish(false);
          setWpid(0);
          setAddWishlistLoading(false);
        }
      } catch (error: unknown) {
        toast.show(errorUtils.getErrorMessage(error));
        setAddWishlistLoading(false);
      }
    } else {
      try {
        if (!hasLogin) {
          // 未登录状态，不允许请求加入心愿单。弹起登录弹窗
          setShowWishLoginModal(true);
          return;
        }

        setAddWishlistLoading(true);
        const res = await productApi.addWishList({
          productID: productDetailData?.productID,
        });
        if (res.data) {
          setIsWish(true);
          setWpid(res.data.wishProductID);
          toast.show("Successfully!");
          setAddWishlistLoading(false);
        }
      } catch (error: unknown) {
        toast.show(errorUtils.getErrorMessage(error));
        setAddWishlistLoading(false);
      }
    }
  };

  const handleLogin = () => {
    const currentPath = window.location.pathname + window.location.search;
    const encodedRedirect = encodeURIComponent(currentPath);

    window.location.href = `/login?redirect=${encodedRedirect}`;
  };

  return (
    <div className={styles["comp-product-shipping"]}>
      {hasLogin ? <ExpediteDialog></ExpediteDialog> : null}
      <div className={styles["afterpay-benefit"]}>
        4 installments with
        <Image
          className={styles["afterpay-icon"]}
          style={{ marginTop: "2px" }}
          src={`${process.env.IMAGE_DOMAIN}/product-detail/icon-afterpay.png`}
          alt="afterpay"
          width={69}
          height={13}
          onClick={() => setShowAfterPayDialog(true)}
        />
        {ZIP_PAYMENT_ENABLED && (
          <>
            or
            <Image
              className={styles["afterpay-icon"]}
              src={`${process.env.IMAGE_DOMAIN}/product-detail/icon-zippay.png`}
              alt="zippay"
              width={36}
              height={13}
              onClick={() => setShowZippayDialog(true)}
            />
          </>
        )}
      </div>

      <div className={styles["quantity-box"]}>
        <div className={styles["quantity-title"]}>Quantity:</div>

        <div
          className={`${styles["quantity-select-wrap"]} ${
            isQuantityDisabled ? styles["quantity-select-wrap-disabled"] : ""
          }`}
        >
          <select
            value={quantity}
            onChange={handleQuantityChange}
            className={styles["quantity-select"]}
            disabled={isQuantityDisabled}
          >
            {[
              ...Array.from({ length: quantityLimit }, (_, i) => i + 1).map(
                (num) => (
                  <option key={num} value={num}>
                    {num == 100 ? "100(max)" : num}
                  </option>
                ),
              ),
            ]}
          </select>
        </div>

        <div className={styles["status-box"]}>
          <div
            className={`
              ${styles["status-sign"]} 
              ${isAlmostSoldOut ? styles["sign-almost-sold-out"] : ""}
              ${isSoldOut ? styles["sign-sold-out"] : ""}
            `}
          ></div>
          {isPreOrder && (
            <div className={styles["status-text"]}>Pre-order </div>
          )}
          {isInStock && <div className={styles["status-text"]}>In Stock</div>}
          {isAlmostSoldOut && (
            <div className={styles["status-text"]}>Almost Sold out</div>
          )}
          {isSoldOut && (
            <div
              className={`${styles["status-text"]} ${styles["status-text-sold-out"]}`}
            >
              Sold out
            </div>
          )}
        </div>
      </div>

      {/* 多规格属性 */}
      {!!(multipleSpecList && multipleSpecList.length) && (
        <div className={styles["multiple-spec-box"]}>
          {multipleSpecList.map((item: SpecOptionType, index: number) => (
            <div className={styles["item"]} key={`multiple-${index}`}>
              <div className={styles["label"]}>
                {item.name.charAt(0).toUpperCase() + item.name.slice(1)}:
              </div>
              <select
                value={Number(item.defaultVariant)}
                style={{ width: "60%" }}
                onChange={(e) => {
                  handleChangeSpec(item.optionID, Number(e.target.value));
                }}
              >
                {item.options?.map((option: any) => (
                  <option key={option.variantID} value={option.variantID}>
                    {option.optionValue}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      <div className={styles["shipping-box-wrap"]}>
        <div className={styles["shipping-box"]}>
        <div className={styles["suburb-select"]}>
          <div className={styles["suburb-select-box"]}>
            <div style={{ flexShrink: 0, marginRight: 8 }}>You Suburb:</div>
            <SuburbSelectDropdown
              className={styles["product-suburb-select"]}
              value={suburbId}
              onChange={suburbChange}
              notAutoSelect={true}
            />
          </div>
        </div>

        <div className={styles["postage-box"]}>
          {!!shippingError && (
            <div className={styles["error-message"]}>Not available.</div>
          )}
          {!!shippingDistanceError && (
            <div className={styles["error-message"]}>Non-delivery areas. </div>
          )}

          <div className={styles["postage-item"]}>
            <div className={styles["title"]}>Delivery & Handling: </div>
            <div className={styles["value"]}>
              {!!postageLoading && "Calculating..."}
              {!!(
                !postageLoading &&
                productDetailData?.enabled &&
                !postageData.postage &&
                postageData.courierId
              ) && "Free Shipping"}
              {!!(
                !postageLoading &&
                !productDetailData?.enabled &&
                !postageData.postage &&
                postageData.courierId
              ) && "Unavailable"}
              {!!(
                !postageLoading &&
                postageData.postage &&
                postageData.courierId
              ) && `$${postageData.postage}`}
            </div>
          </div>

          <div className={styles["postage-item"]}>
            <div className={styles["title"]}>ETA: </div>
            <div className={styles["value"]}>
              {!postageLoading && postageData.eta}
            </div>
          </div>

          {!!productDetailData?.isDropShipping && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>
                Ships from and sold by Globaloutlet
              </div>
            </div>
          )}

          {!!(productDetailData?.courierId == 9) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>No Delivery to PO Box.</div>
            </div>
          )}

          {!!(productDetailData?.courierId == 27) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]}>
                This item only delivers to Melbourne metro area.
              </div>
            </div>
          )}

          {!!(
            productDetailData?.preSales && productDetailData?.preSalesDate
          ) && (
            <div className={styles["postage-item"]}>
              <div className={styles["title"]} style={{ color: "#e6415f" }}>
                *This is a PRE-ORDER item, estimated shipping on{" "}
                {dayjs(productDetailData?.preSalesDate).format("DD/MM/YYYY")}
              </div>
            </div>
          )}
        </div>

        {productDetailData?.courierId &&
          [3, 5, 10].includes(parseInt(`${productDetailData?.courierId}`)) && (
            <div
              className={styles["help-line"]}
              onClick={handleShowCombineModal}
            >
              <Image
                className={styles["shipping-save"]}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/shipping-save.svg`}
                alt="shipping-save"
                width={24}
                height={13}
              />
              <div className={styles["shipping-save-text"]}>
                Combine Shipping & Save
              </div>
              Help
              <Image
                className={styles["icon-help"]}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/icon-help.svg`}
                alt="icon-help"
                width={24}
                height={13}
              />
            </div>
          )}
        </div>
      </div>

      {(() => {
        const allowedPaymentMethods = [
          "VISA",
          "Mastercard",
          "PayPal",
          "Afterpay",
          ...(ZIP_PAYMENT_ENABLED ? ["zip", "ZIP"] : []),
          "Apple Pay",
          "Google Pay",
        ];
        const filteredPaymentMethod =
          paymentMethod?.filter(
            (item) =>
              isPaymentMethodVisible(item) &&
              allowedPaymentMethods.some((method) =>
                item.paymentName?.toLowerCase().includes(method.toLowerCase()),
              ),
          ) || [];
        const supplementalPaymentMethods: paymentMethodItem[] = [
          {
            paymentName: "Apple Pay",
            paymentIcon: `${process.env.IMAGE_DOMAIN}/svg/payment-method/Applepay.svg`,
          },
          {
            paymentName: "Google Pay",
            paymentIcon: `${process.env.IMAGE_DOMAIN}/svg/payment-method/google-pay.svg`,
          },
        ].filter(
          (method) =>
            !filteredPaymentMethod.some((item) =>
              item.paymentName
                ?.toLowerCase()
                .includes(method.paymentName.toLowerCase()),
            ),
        );
        const displayPaymentMethods = [
          ...filteredPaymentMethod,
          ...supplementalPaymentMethods,
        ];
        return displayPaymentMethods.length ? (
          <div className={styles["payment-method-box"]}>
            <div className={styles["payment-method-title"]}>
              We proudly accept:
            </div>
            <div className={styles["payment-method-list"]}>
              {displayPaymentMethods.map((item, index: number) => (
                <Image
                  key={index}
                  className={`${styles["payment-img"]}`}
                  src={item.paymentIcon}
                  alt={item.paymentName}
                  width={57}
                  height={32}
                />
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* 商品状态的对应按钮 */}
      <div className={styles["btn-box"]}>
        <StockStatusBtn></StockStatusBtn>

        {/* 加入心愿单 */}
        <Button
          style={{
            marginTop: "15px",
            border: "1px solid #333333",
          }}
          block
          loading={addWishlistLoading}
          loadingText="Add to Wishlist"
          onClick={handleToggleWish}
        >
          <div className={styles["add-wishlist-content"]}>
            <Image
              className={styles["wishlist-icon"]}
              src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/${
                isWish ? "my-wishlist-click" : "my-wishlist"
              }.svg`}
              alt="wishlist"
              width={21}
              height={21}
            />
            Add to Wishlist
          </div>
        </Button>
      </div>

      {productDetailData?.warehouseID === 100002 && (
        <div className={styles["contact-us"]}>
          Wholesale prices are available,
          <span
            className={styles["link"]}
            onClick={() => setShowWholesaleModal(true)}
          >
            contact us
          </span>
          for more discounts on bulk buys.
        </div>
      )}

      {productDetailData?.inStock &&
      productDetailData?.enabled &&
      !productDetailData.preSales ? (
        <div className={styles["float-btn"]}>
          <Button
            type="primary"
            block
            className={styles["add-cart"]}
            onClick={(event) => {
              addToCart(
                event,
                {
                  productID: productDetailData.productID,
                  qty: quantity,
                },
                (res: any) => {
                  addToCartCallBack(res);
                },
              );
            }}
          >
            Add to Cart
          </Button>
        </div>
      ) : null}

      {productDetailData.preSales ? (
        <div className={styles["float-btn"]}>
          <Button
            type="primary"
            block
            className={styles["pre-sales-btn"]}
            onClick={preOrderNow}
          >
            Pre Sales
          </Button>
        </div>
      ) : null}

      <Dialog
        visible={showAfterPayDialog}
        title=""
        closeable={true}
        showConfirmButton={false}
        showCancelButton={false}
        onCancel={() => setShowAfterPayDialog(false)}
      >
        <div className={styles["afterpay-model"]}>
          <Image
            className={styles["logo"]}
            src={`${process.env.IMAGE_DOMAIN}/afterpay/afterpay-toast.jpg`}
            alt="afterpay"
            width={178}
            height={60}
          />

          <div className={styles["simply-checkout"]}>
            <div className={styles["simply-title"]}>All you need is</div>
            <p className={styles['simply-desc']}>Must be over 18 years old; Live in New Zealand;</p>
            <p className={styles['simply-desc']}>Have a New Zealand debit or credit card.</p>
            <p className={styles['simply-desc']}>
              Simply select <span>afterpay</span> at checkout.
            </p>
            <div className={styles["afterpay-payment-card"]}>
              <div className={styles["afterpay-logo-row"]}>
                <span className={styles["afterpay-dot"]}></span>
                <Image
                  className={styles["afterpay-logo-small"]}
                  src={`${process.env.IMAGE_DOMAIN}/svg/pay/icon-payment-logo-17.svg`}
                  alt="afterpay"
                  width={132}
                  height={25}
                />
              </div>
              <div className={styles["afterpay-payment-text"]}>
                Interest-free payments.
              </div>
            </div>
          </div>

          <div
            className={styles["got-it"]}
            onClick={() => setShowAfterPayDialog(false)}
          >
            Got it!
          </div>

          <div className={styles["apply"]}>
            <Link
              href="https://www.afterpay.com/en-NZ/terms-of-service"
              className={styles["apply-link"]}
              onClick={() => setShowAfterPayDialog(false)}
              rel="noopener noreferrer"
              target="_blank"
            >
              Terms &amp; Conditions
            </Link>
            apply.
          </div>

          <div className={styles["text"]}>
            {"\u2460"} For first time Afterpay customers the first payment will
            be made at the time of purchase, with payments over 6 weeks.
            Existing Afterpay customers first payment will be made in 14 days,
            with payments over 8 weeks. The full payment schedule will be
            provided to you at time of purchase.
          </div>
          <div className={styles["text"]}>
            {"\u2461"} If you fail to have sufficient funds available for
            Afterpay to automatically deduct payment on due instalment dates you
            will be charged a late fee of $10 and if the installment payment
            remains overdue one week late, an additional $7 will be charged.
          </div>
        </div>
      </Dialog>

      {ZIP_PAYMENT_ENABLED && (
        <Dialog
          visible={showZippayDialog}
          title=""
          closeable={true}
          showConfirmButton={true}
          showCancelButton={false}
          confirmButtonText="OK"
          onCancel={() => setShowZippayDialog(false)}
          onConfirm={() => {
            setShowZippayDialog(false);
          }}
        >
          <div
            style={{
              width: "100%",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <Image
              style={{
                display: "block",
                width: "100%",
                height: "auto",
                maxWidth: "100%",
              }}
              src={`${process.env.IMAGE_DOMAIN}/product-detail/zippay-explain.jpg`}
              alt="zippay"
              width={488}
              height={595}
            />
          </div>
        </Dialog>
      )}

      <Dialog
        visible={showCombineModal}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="OK"
        onCancel={() => setShowCombineModal(false)}
        onConfirm={() => {
          setShowCombineModal(false);
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              width: "100%",
              padding: "10px 15px",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                marginBottom: "15px",
                wordWrap: "break-word",
                wordBreak: "break-word",
                textAlign: "center",
                color: "#e6415f",
              }}
            >
              Save Money on Your Orders, Combine Shipping!
            </div>

            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Why not grab more than one item and save on shipping? To take
              advantage, just add two or more products to your shopping cart.
              <br />(
              <strong>
                Products eligible for combined shipping are marked
              </strong>
              <Image
                style={{
                  display: "inline-block",
                  width: "18px",
                  height: "auto",
                  maxWidth: "100%",
                }}
                src={`${process.env.IMAGE_DOMAIN}/svg/product-detail/shipping-save.svg`}
                alt="shipping-save"
                width={18}
                height={13}
              />
              <span style={{ color: "#e6415f", fontWeight: "bold" }}>
                as Combine Shipping &amp; Save
              </span>
              ).
            </div>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              After you have finished shopping, proceed to checkout to see how
              much you have saved on shipping.
            </div>
            <p
              style={{
                marginTop: "8px",
                marginBottom: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              Simply finalise payment on your order! Remember, there are greater
              savings to be had by purchasing multiple eligible products!
            </p>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              <strong>Terms &amp; Conditions:</strong>
              <ul
                style={{
                  marginTop: "5px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  If your order also contains items that are not eligible for
                  Combined Shipping, you will be charged normal shipping rates
                  on those items.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  If any discount offer on shipping fees are used on eligible
                  items such as a coupon discount code, the item in question
                  will not be eligible for any further shipping discounts.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  Actual savings will vary depending on the weight of the item
                  and it&#039;s category.
                </li>
              </ul>
            </div>
            <div
              style={{
                marginTop: "8px",
                wordWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              <strong>FAQ</strong>
              <p
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                Why am I unable to receive any discounts when purchasing two or
                more products that are eligible for combined shipping?
              </p>
              <p
                style={{
                  marginTop: "5px",
                  marginBottom: "5px",
                  wordWrap: "break-word",
                  wordBreak: "break-word",
                }}
              >
                We have 2 exceptions to the combine shipping system:
              </p>
              <ul
                style={{
                  marginTop: "5px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  a) Due to minimum charges set by courier companies, there may
                  be no additional shipping discount for individual items
                  weighing less than 0.5kg.
                </li>
                <li
                  style={{
                    marginBottom: "5px",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                  }}
                >
                  b) Items shipped via DHL / UPS or regular mail can only be
                  combined with multiple of the exact same product to receive a
                  combined shipping discount.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showWishLoginModal}
        title=""
        closeable={true}
        showConfirmButton={true}
        showCancelButton={false}
        confirmButtonText="Confirm"
        onCancel={() => setShowWishLoginModal(false)}
        onConfirm={() => {
          setShowWishLoginModal(false);
          handleLogin();
        }}
      >
        <div
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#333333",
              paddingTop: "10px",
              wordWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            Please login to add product to your wishlist!
          </div>
        </div>
      </Dialog>

      <NotifyDialog
        productID={String(productDetailData.productID)}
        showNotify={showNotifyModal}
        onClose={() => setShowNotifyModal(false)}
      ></NotifyDialog>

      <WholesaleDialog
        productID={String(productDetailData.productID)}
        productUrl={`${process.env.API_DOMAIN}${productDetailData?.url}`}
        showWholesale={showWholesaleModal}
        onClose={() => setShowWholesaleModal(false)}
      ></WholesaleDialog>
    </div>
  );
}
